import React, { useMemo } from "react";
import { Table } from "antd";
import { type ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";

import { type MarketingLead } from "@fitness-app/data-models/entities/MarketingLead";

import { type MarketingCampaignRow } from "../hooks/useMarketingCampaigns";
import { cacColor, formatNumber, formatPercent, formatPln, roasColor, srColor } from "../marketingFormat";
import { calculateMarketingCampaignTotals } from "../marketingMetrics";
import { CampaignLeadsPanel } from "./CampaignLeadsPanel";

interface Props {
  campaigns: MarketingCampaignRow[];
  leads: MarketingLead[];
  loading: boolean;
}

const NO_CAMPAIGN_KEY = "__none__";

const campaignLeadsKey = (campaignId: string | null): string => campaignId ?? NO_CAMPAIGN_KEY;

const NUMERIC_CELL_CLASS_NAME = "tabular-nums";

const colored = (className: string, content: React.ReactNode) => (
  <span className={`-mx-2 inline-block rounded px-2 py-0.5 tabular-nums ${className}`}>{content}</span>
);

export const CampaignsTable: React.FC<Props> = ({ campaigns, leads, loading }) => {
  const { t } = useTranslation("dashboard");
  const totals = useMemo(() => calculateMarketingCampaignTotals(campaigns), [campaigns]);
  const leadsByCampaign = useMemo(() => {
    const grouped = new Map<string, MarketingLead[]>();
    for (const lead of leads) {
      const key = campaignLeadsKey(lead.normalizedCampaignId);
      const bucket = grouped.get(key);
      if (bucket) {
        bucket.push(lead);
      } else {
        grouped.set(key, [lead]);
      }
    }
    return grouped;
  }, [leads]);
  const columns = useMemo<ColumnsType<MarketingCampaignRow>>(
    () => [
      {
        title: t("marketingAnalytics.campaigns.columns.campaign"),
        dataIndex: "campaignName",
        key: "campaignName",
        fixed: "left",
        width: 200,
      },
      {
        title: t("marketingAnalytics.campaigns.columns.spend"),
        dataIndex: "spend",
        key: "spend",
        align: "right",
        className: NUMERIC_CELL_CLASS_NAME,
        render: formatPln,
      },
      {
        title: t("marketingAnalytics.campaigns.columns.bookingsTotal"),
        dataIndex: "bookingsTotal",
        key: "bookingsTotal",
        align: "right",
        className: NUMERIC_CELL_CLASS_NAME,
        render: formatNumber,
      },
      {
        title: t("marketingAnalytics.campaigns.columns.bookingsPast"),
        dataIndex: "bookingsPast",
        key: "bookingsPast",
        align: "right",
        className: NUMERIC_CELL_CLASS_NAME,
        render: formatNumber,
      },
      {
        title: t("marketingAnalytics.campaigns.columns.bookingsFuture"),
        dataIndex: "bookingsFuture",
        key: "bookingsFuture",
        align: "right",
        className: NUMERIC_CELL_CLASS_NAME,
        render: formatNumber,
      },
      {
        title: t("marketingAnalytics.campaigns.columns.showed"),
        dataIndex: "showed",
        key: "showed",
        align: "right",
        className: NUMERIC_CELL_CLASS_NAME,
        render: formatNumber,
      },
      {
        title: t("marketingAnalytics.campaigns.columns.showRate"),
        dataIndex: "srPercent",
        key: "srPercent",
        align: "right",
        className: NUMERIC_CELL_CLASS_NAME,
        render: (value: unknown) => colored(srColor(value), formatPercent(value)),
      },
      {
        title: t("marketingAnalytics.campaigns.columns.costPerBooking"),
        dataIndex: "costPerBooking",
        key: "costPerBooking",
        align: "right",
        className: NUMERIC_CELL_CLASS_NAME,
        render: formatPln,
      },
      {
        title: t("marketingAnalytics.campaigns.columns.costPerShow"),
        dataIndex: "costPerShow",
        key: "costPerShow",
        align: "right",
        className: NUMERIC_CELL_CLASS_NAME,
        render: formatPln,
      },
      {
        title: t("marketingAnalytics.campaigns.columns.sales"),
        dataIndex: "sales",
        key: "sales",
        align: "right",
        className: NUMERIC_CELL_CLASS_NAME,
        render: formatNumber,
      },
      {
        title: t("marketingAnalytics.campaigns.columns.contractedValue"),
        dataIndex: "contractedValue",
        key: "contractedValue",
        align: "right",
        className: NUMERIC_CELL_CLASS_NAME,
        render: formatPln,
      },
      {
        title: t("marketingAnalytics.campaigns.columns.roas"),
        dataIndex: "roas",
        key: "roas",
        align: "right",
        className: NUMERIC_CELL_CLASS_NAME,
        render: (value: unknown) => colored(roasColor(value), formatNumber(value)),
      },
      {
        title: t("marketingAnalytics.campaigns.columns.cac"),
        dataIndex: "cac",
        key: "cac",
        align: "right",
        className: NUMERIC_CELL_CLASS_NAME,
        render: (value: unknown) => colored(cacColor(value), formatPln(value)),
      },
    ],
    [t],
  );

  return (
    <Table<MarketingCampaignRow>
      rowKey={(row) => row.campaignId ?? row.campaignName}
      columns={columns}
      dataSource={campaigns}
      loading={loading}
      pagination={false}
      scroll={{ x: 1400 }}
      size="middle"
      expandable={{
        expandedRowRender: (row) => (
          <CampaignLeadsPanel leads={leadsByCampaign.get(campaignLeadsKey(row.campaignId)) ?? []} />
        ),
      }}
      summary={() => (
        <Table.Summary fixed>
          <Table.Summary.Row className="font-semibold">
            {/* Index 0 is the expand-toggle column added by `expandable`. */}
            <Table.Summary.Cell index={0} />
            <Table.Summary.Cell index={1}>{t("marketingAnalytics.campaigns.total")}</Table.Summary.Cell>
            <Table.Summary.Cell index={2} align="right">
              {formatPln(totals.spend)}
            </Table.Summary.Cell>
            <Table.Summary.Cell index={3} align="right">
              {formatNumber(totals.bookingsTotal)}
            </Table.Summary.Cell>
            <Table.Summary.Cell index={4} align="right">
              {formatNumber(totals.bookingsPast)}
            </Table.Summary.Cell>
            <Table.Summary.Cell index={5} align="right">
              {formatNumber(totals.bookingsFuture)}
            </Table.Summary.Cell>
            <Table.Summary.Cell index={6} align="right">
              {formatNumber(totals.showed)}
            </Table.Summary.Cell>
            <Table.Summary.Cell index={7} align="right">
              {colored(srColor(totals.srPercent), formatPercent(totals.srPercent))}
            </Table.Summary.Cell>
            <Table.Summary.Cell index={8} align="right">
              {formatPln(totals.costPerBooking)}
            </Table.Summary.Cell>
            <Table.Summary.Cell index={9} align="right">
              {formatPln(totals.costPerShow)}
            </Table.Summary.Cell>
            <Table.Summary.Cell index={10} align="right">
              {formatNumber(totals.sales)}
            </Table.Summary.Cell>
            <Table.Summary.Cell index={11} align="right">
              {formatPln(totals.contractedValue)}
            </Table.Summary.Cell>
            <Table.Summary.Cell index={12} align="right">
              {colored(roasColor(totals.roas), formatNumber(totals.roas))}
            </Table.Summary.Cell>
            <Table.Summary.Cell index={13} align="right">
              {colored(cacColor(totals.cac), formatPln(totals.cac))}
            </Table.Summary.Cell>
          </Table.Summary.Row>
        </Table.Summary>
      )}
    />
  );
};
